import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, FormGroup, FormArray, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, forkJoin, map, of } from 'rxjs';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { DividerModule } from 'primeng/divider';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ComplaintService, ComplaintModel, VehicleInvolvedModel, ComplaintRequestDTO } from '../../services/complaint.service';
import { ComplaintStatusService, ComplaintStatusModel } from '../../services/complaint-status.service';
import { ComplaintTypeService, ComplaintTypeModel } from '../../services/complaint-type.service';
import { BarramentoService, VehicleBarramentoResponse } from '../../services/barramento.service';
import { VehicleInvolvedService, VehicleInvolvedRequestDTO } from '../../services/vehicle-involved.service';

@Component({
  selector: 'app-complaint-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    DividerModule,
    ToolbarModule,
    ToastModule,
  ],
  providers: [MessageService],
  template: `
    <p-card *ngIf="complaint; else loadingTpl">
      <ng-template pTemplate="header">
        <p-toolbar>
          <ng-template pTemplate="start">
            <h2>{{ isNew ? 'Nova Denúncia' : 'Editar Denúncia #' + complaint.id }}</h2>
          </ng-template>
          <ng-template pTemplate="end">
            <p-button
              label="Cancelar"
              icon="pi pi-times"
              (onClick)="cancel()"
              severity="secondary"
              [outlined]="true"
              class="mr-2">
            </p-button>
            <p-button
              label="Salvar"
              icon="pi pi-check"
              (onClick)="save()"
              severity="success"
              [disabled]="form.invalid || saving">
            </p-button>
          </ng-template>
        </p-toolbar>
      </ng-template>

      <ng-template pTemplate="content">
        <form [formGroup]="form" class="edit-form">
          <div class="form-grid">
            <!-- Tipo da Denúncia -->
            <div class="field">
              <label for="complaintType">Tipo da Denúncia *</label>
              <p-select
                inputId="complaintType"
                formControlName="complaintType"
                [options]="typeOptions"
                optionLabel="description"
                placeholder="Selecione o tipo"
                [showClear]="true"
                class="w-full">
              </p-select>
            </div>

            <!-- Status -->
            <div class="field">
              <label for="complaintStatus">Status *</label>
              <p-select
                inputId="complaintStatus"
                formControlName="complaintStatus"
                [options]="statusOptions"
                optionLabel="description"
                placeholder="Selecione o status"
                [showClear]="true"
                class="w-full">
              </p-select>
            </div>

            <!-- Endereço -->
            <div class="field field-full">
              <label for="address">Endereço</label>
              <input
                id="address"
                pInputText
                formControlName="address"
                placeholder="Digite o endereço"
                class="w-full" />
            </div>

            <!-- Latitude -->
            <div class="field">
              <label for="latitude">Latitude</label>
              <input
                id="latitude"
                pInputText
                formControlName="latitude"
                placeholder="Ex: -3.71722"
                class="w-full" />
            </div>

            <!-- Longitude -->
            <div class="field">
              <label for="longitude">Longitude</label>
              <input
                id="longitude"
                pInputText
                formControlName="longitude"
                placeholder="Ex: -38.54306"
                class="w-full" />
            </div>

            <!-- Observação -->
            <div class="field field-full">
              <label for="text">Observação</label>
              <textarea
                id="text"
                formControlName="text"
                rows="5"
                placeholder="Descreva a denúncia..."
                class="w-full"
                style="min-height: 120px; resize: vertical; padding: 0.75rem; border: 1px solid var(--surface-border, #d1d5db); border-radius: 6px; font-family: inherit;">
              </textarea>
            </div>
          </div>

          <!-- ============================================================ -->
          <!-- Veículos Envolvidos -->
          <!-- ============================================================ -->
          <p-divider></p-divider>
          <h3 class="section-title">
            <i class="pi pi-car mr-2"></i> Veículos Envolvidos
          </h3>

          <div formArrayName="vehicles">
            <div
              *ngFor="let vehicleGroup of vehicles.controls; let vIdx = index"
              [formGroupName]="vIdx"
              class="vehicle-card">

              <!-- Cabeçalho do veículo -->
              <div class="vehicle-header">
                <h4>Veículo #{{ vIdx + 1 }}</h4>
                <p-button
                  *ngIf="vehicles.length > 0"
                  icon="pi pi-trash"
                  severity="danger"
                  [outlined]="true"
                  size="small"
                  (onClick)="removeVehicle(vIdx)"
                  title="Remover veículo">
                </p-button>
              </div>

              <div class="form-grid">
                <!-- Placa + Buscar -->
                <div class="field">
                  <label>Placa</label>
                  <div class="placa-search-row">
                    <input
                      pInputText
                      formControlName="plateNumber"
                      placeholder="ABC-1234"
                      class="w-full" />
                    <p-button
                      icon="pi pi-search"
                      severity="info"
                      [outlined]="true"
                      [loading]="searchingPlate[vIdx]"
                      (onClick)="searchPlate(vIdx)"
                      title="Buscar dados pela placa">
                    </p-button>
                  </div>
                </div>
                <!-- Marca/Modelo -->
                <div class="field">
                  <label>Marca/Modelo</label>
                  <input pInputText formControlName="brandModel" placeholder="Fiat/Uno" class="w-full" />
                </div>
                <!-- Cor -->
                <div class="field">
                  <label>Cor</label>
                  <input pInputText formControlName="color" placeholder="Branco" class="w-full" />
                </div>
                <!-- Município -->
                <div class="field">
                  <label>Município</label>
                  <input pInputText formControlName="municipality" placeholder="Fortaleza" class="w-full" />
                </div>
                <!-- Estado -->
                <div class="field">
                  <label>Estado (UF)</label>
                  <input pInputText formControlName="state" placeholder="CE" maxlength="2" class="w-full" />
                </div>
                <!-- Tipo -->
                <div class="field">
                  <label>Tipo</label>
                  <input pInputText formControlName="type" placeholder="Automóvel" class="w-full" />
                </div>
                <!-- Espécie -->
                <div class="field">
                  <label>Espécie</label>
                  <input pInputText formControlName="species" placeholder="Passageiro" class="w-full" />
                </div>
                <!-- Categoria -->
                <div class="field">
                  <label>Categoria</label>
                  <input pInputText formControlName="category" placeholder="Particular" class="w-full" />
                </div>
                <!-- Ano Fabricação -->
                <div class="field">
                  <label>Ano Fabricação</label>
                  <input pInputText formControlName="yearOfManufacture" placeholder="2020" class="w-full" />
                </div>
                <!-- Ano Modelo -->
                <div class="field">
                  <label>Ano Modelo</label>
                  <input pInputText formControlName="yearOfModel" placeholder="2021" class="w-full" />
                </div>
                <!-- Último Licenciamento -->
                <div class="field">
                  <label>Último Licenciamento</label>
                  <input pInputText formControlName="lastLicensingYear" placeholder="2024" class="w-full" />
                </div>
                <!-- VIN/Chassi -->
                <div class="field">
                  <label>Chassi (VIN)</label>
                  <input pInputText formControlName="vin" placeholder="9BW..." class="w-full" />
                </div>
              </div>

              <!-- ============================================================ -->
              <!-- Proprietários do veículo -->
              <!-- ============================================================ -->
              <p-divider></p-divider>
              <h5 class="subsection-title">
                <i class="pi pi-user mr-2"></i> Proprietários
              </h5>

              <div formArrayName="owners">
                <div
                  *ngFor="let ownerGroup of getVehicleOwners(vIdx).controls; let oIdx = index"
                  [formGroupName]="oIdx"
                  class="owner-card">

                  <div class="owner-header">
                    <span>Proprietário #{{ oIdx + 1 }}</span>
                    <p-button
                      icon="pi pi-trash"
                      severity="danger"
                      [outlined]="true"
                      size="small"
                      (onClick)="removeOwner(vIdx, oIdx)"
                      title="Remover proprietário">
                    </p-button>
                  </div>

                  <div class="form-grid">
                    <div class="field">
                      <label>Nome</label>
                      <input pInputText formControlName="name" placeholder="Nome completo" class="w-full" />
                    </div>
                    <div class="field">
                      <label>CPF/CNPJ</label>
                      <input pInputText formControlName="document" placeholder="000.000.000-00" class="w-full" />
                    </div>
                  </div>

                  <!-- Endereço do proprietário -->
                  <div class="owner-address-header">Endereço</div>
                  <div formGroupName="address" class="form-grid">
                    <div class="field field-full">
                      <label>Logradouro</label>
                      <input pInputText formControlName="streetName" placeholder="Rua Exemplo" class="w-full" />
                    </div>
                    <div class="field">
                      <label>Número</label>
                      <input pInputText formControlName="number" placeholder="123" class="w-full" />
                    </div>
                    <div class="field">
                      <label>Complemento</label>
                      <input pInputText formControlName="complement" placeholder="Apto 101" class="w-full" />
                    </div>
                    <div class="field">
                      <label>Bairro</label>
                      <input pInputText formControlName="neighborhood" placeholder="Centro" class="w-full" />
                    </div>
                    <div class="field">
                      <label>Cidade</label>
                      <input pInputText formControlName="city" placeholder="Fortaleza" class="w-full" />
                    </div>
                    <div class="field">
                      <label>Estado (UF)</label>
                      <input pInputText formControlName="state" placeholder="CE" maxlength="2" class="w-full" />
                    </div>
                    <div class="field">
                      <label>CEP</label>
                      <input pInputText formControlName="postalCode" placeholder="60000-000" class="w-full" />
                    </div>
                    
                  </div>
                </div>
              </div>

              <!-- Botão adicionar proprietário -->
              <p-button
                icon="pi pi-plus"
                label="Adicionar Proprietário"
                severity="info"
                [outlined]="true"
                size="small"
                class="mt-2"
                (onClick)="addOwner(vIdx)">
              </p-button>
            </div>
          </div>

          <!-- Botão adicionar veículo -->
          <p-button
            icon="pi pi-plus"
            label="Adicionar Veículo"
            severity="secondary"
            [outlined]="true"
            class="mt-3"
            (onClick)="addVehicle()">
          </p-button>

        </form>
      </ng-template>
    </p-card>

    <ng-template #loadingTpl>
      <div class="loading-container">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
        <p>Carregando...</p>
      </div>
    </ng-template>

    <p-toast></p-toast>
  `,
  styles: [`
    .edit-form {
      padding: 0.5rem 0;
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.25rem;
    }

    .field {
      display: flex;
      flex-direction: column;
      gap: 0.35rem;
    }

    .field label {
      font-weight: 600;
      color: var(--text-color-secondary, #6b7280);
      font-size: 0.9rem;
    }

    .field-full {
      grid-column: 1 / -1;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 4rem;
      color: var(--text-color-secondary, #6b7280);
      gap: 1rem;
    }

    .w-full {
      width: 100%;
    }

    /* ===== Veículos e Proprietários ===== */

    .section-title {
      font-size: 1.15rem;
      font-weight: 700;
      color: var(--text-color, #374151);
      margin: 1.5rem 0 1rem;
      display: flex;
      align-items: center;
    }

    .subsection-title {
      font-size: 1rem;
      font-weight: 600;
      color: var(--text-color-secondary, #6b7280);
      margin: 0.5rem 0;
      display: flex;
      align-items: center;
    }

    .vehicle-card {
      border: 1px solid var(--surface-border, #e5e7eb);
      border-radius: 10px;
      padding: 1.25rem;
      margin-bottom: 1rem;
      background: var(--surface-card, #fff);
    }

    .vehicle-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
    }

    .vehicle-header h4 {
      margin: 0;
      font-size: 1.05rem;
      font-weight: 600;
      color: var(--primary-color, #3b82f6);
    }

    .owner-card {
      border: 1px dashed var(--surface-border, #cbd5e1);
      border-radius: 8px;
      padding: 1rem;
      margin-bottom: 0.75rem;
      background: var(--surface-ground, #f9fafb);
    }

    .owner-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.75rem;
      font-weight: 600;
      color: var(--text-color-secondary, #6b7280);
    }

    .owner-address-header {
      font-weight: 600;
      color: var(--text-color-secondary, #6b7280);
      margin: 0.75rem 0 0.5rem;
      font-size: 0.9rem;
    }

    .mt-2 {
      margin-top: 0.5rem;
    }

    .mt-3 {
      margin-top: 1rem;
    }

    .placa-search-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .placa-search-row input {
      flex: 1;
    }

    .placa-search-row .p-button {
      flex-shrink: 0;
    }

    :host ::ng-deep .p-toolbar {
      border: none;
      padding: 1rem 0;
    }
  `]
})
export class ComplaintEditComponent implements OnInit {
  complaint: ComplaintModel | null = null;
  isNew = false;
  saving = false;

  typeOptions: ComplaintTypeModel[] = [];
  statusOptions: ComplaintStatusModel[] = [];

  form = new FormGroup({
    complaintType: new FormControl<ComplaintTypeModel | null>(null, Validators.required),
    complaintStatus: new FormControl<ComplaintStatusModel | null>(null, Validators.required),
    address: new FormControl(''),
    latitude: new FormControl(''),
    longitude: new FormControl(''),
    text: new FormControl(''),
    vehicles: new FormArray<FormGroup>([]),
  });

  /** Acesso rápido ao FormArray de veículos */
  get vehicles(): FormArray<FormGroup> {
    return this.form.get('vehicles') as FormArray<FormGroup>;
  }

  /** Retorna o FormArray de proprietários de um veículo pelo índice */
  getVehicleOwners(vehicleIndex: number): FormArray<FormGroup> {
    return this.vehicles.at(vehicleIndex).get('owners') as FormArray<FormGroup>;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private complaintService: ComplaintService,
    private complaintStatusService: ComplaintStatusService,
    private complaintTypeService: ComplaintTypeService,
    private barramentoService: BarramentoService,
    private vehicleInvolvedService: VehicleInvolvedService,
    private messageService: MessageService,
  ) {}

  /** Controla loading individual do botão buscar por veículo */
  searchingPlate: boolean[] = [];

  ngOnInit(): void {
    this.loadStatuses();
    this.loadTypes();

    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (isNaN(id)) {
      this.isNew = true;
      this.complaint = { id: 0, registerDate: new Date().toISOString().split('T')[0] };
    } else {
      this.isNew = false;
      this.complaintService.findById(id).subscribe({
        next: (data) => {
          this.complaint = data;
          this.patchForm(data);
        },
        error: (err) => {
          console.error('Erro ao carregar denúncia:', err);
          this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível carregar a denúncia.' });
          this.router.navigate(['/home/complaints']);
        }
      });
    }
  }

  loadStatuses(): void {
    this.complaintStatusService.findAll().subscribe({
      next: (data) => this.statusOptions = data,
      error: (err) => console.error('Erro ao carregar status:', err)
    });
  }

  loadTypes(): void {
    this.complaintTypeService.findAll().subscribe({
      next: (data) => this.typeOptions = data,
      error: (err) => console.error('Erro ao carregar tipos:', err)
    });
  }

  patchForm(data: ComplaintModel): void {
    this.form.patchValue({
      complaintType: data.complaintType ?? null,
      complaintStatus: data.complaintStatus ?? null,
      address: data.address ?? '',
      latitude: data.latitude ?? '',
      longitude: data.longitude ?? '',
      text: data.text ?? '',
    });

    // Carrega veículos existentes
    this.vehicles.clear();
    (data.involvedList ?? []).forEach(v => this.vehicles.push(this.createVehicleGroup(v)));
  }

  // ============================================================
  // Veículos
  // ============================================================

  createVehicleGroup(data?: VehicleInvolvedModel): FormGroup {
    const ownersArr = new FormArray<FormGroup>([]);
    (data?.owners ?? []).forEach(o => ownersArr.push(this.createOwnerGroup(o)));

    return new FormGroup({
      plateNumber: new FormControl(data?.plateNumber ?? data?.placa ?? ''),
      brandModel: new FormControl(data?.brandModel ?? data?.marcaModelo ?? ''),
      color: new FormControl(data?.color ?? ''),
      municipality: new FormControl(data?.municipality ?? ''),
      state: new FormControl(data?.state ?? ''),
      type: new FormControl(data?.type ?? ''),
      species: new FormControl(data?.species ?? ''),
      category: new FormControl(data?.category ?? ''),
      yearOfManufacture: new FormControl(data?.yearOfManufacture ?? ''),
      yearOfModel: new FormControl(data?.yearOfModel ?? ''),
      lastLicensingYear: new FormControl(data?.lastLicensingYear ?? ''),
      vin: new FormControl(data?.vin ?? ''),
      owners: ownersArr,
    });
  }

  addVehicle(): void {
    this.vehicles.push(this.createVehicleGroup());
    this.searchingPlate.push(false);
  }

  removeVehicle(index: number): void {
    this.vehicles.removeAt(index);
    this.searchingPlate.splice(index, 1);
  }

  /** Busca dados do veículo pela placa e preenche o formulário */
  searchPlate(vehicleIndex: number): void {
    const vehicleGroup = this.vehicles.at(vehicleIndex);
    const plate: string = (vehicleGroup.get('plateNumber')?.value ?? '').trim();
    if (!plate) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Digite a placa antes de buscar.' });
      return;
    }

    this.searchingPlate[vehicleIndex] = true;

    this.barramentoService.searchByPlaca(plate).subscribe({
      next: (data: VehicleBarramentoResponse) => {
        this.searchingPlate[vehicleIndex] = false;

        if (data.retorno?.codigo === -1) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Placa não encontrada',
            detail: data.retorno?.descricao ?? 'Não foi possível localizar a placa.'
          });
          return;
        }

        // Preenche os campos do veículo com os dados retornados
        vehicleGroup.patchValue({
          plateNumber: data.placa ?? plate,
          brandModel: data.marcaModelo ?? '',
          color: data.cor ?? '',
          municipality: data.municipio ?? '',
          state: data.uf ?? '',
          type: data.tipo ?? '',
          species: data.especie ?? '',
          category: data.categoria ?? '',
          yearOfManufacture: data.anoFabricacao ?? '',
          yearOfModel: data.anoModelo ?? '',
          vin: data.chassi ?? '',
        });

        // Se há proprietário no retorno, adiciona ao primeiro slot de owners
        const ownersArr = this.getVehicleOwners(vehicleIndex);
        const propName = data.proprietario?.trim();
        const propDoc = data.cpfCnpjProprietario?.trim();

        if (propName || propDoc) {
          let filled = false;
          for (let i = 0; i < ownersArr.length; i++) {
            const owner = ownersArr.at(i);
            if (!owner.get('name')?.value && !owner.get('document')?.value) {
              owner.patchValue({ name: propName ?? '', document: propDoc ?? '' });
              filled = true;
              break;
            }
          }
          if (!filled) {
            ownersArr.push(this.createOwnerGroup({ name: propName, document: propDoc }));
          }
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Placa encontrada',
          detail: `Dados de ${data.marcaModelo ?? 'veículo'} preenchidos.`
        });
      },
      error: (err) => {
        this.searchingPlate[vehicleIndex] = false;
        console.error('Erro ao buscar placa:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erro na consulta',
          detail: err?.error?.message ?? 'Falha ao consultar a placa.'
        });
      }
    });
  }

  // ============================================================
  // Proprietários
  // ============================================================

  createOwnerGroup(data?: any): FormGroup {
    return new FormGroup({
      name: new FormControl(data?.name ?? ''),
      document: new FormControl(data?.document ?? ''),
      address: new FormGroup({
        streetName: new FormControl(data?.address?.streetName ?? ''),
        number: new FormControl(data?.address?.number ?? ''),
        complement: new FormControl(data?.address?.complement ?? ''),
        neighborhood: new FormControl(data?.address?.neighborhood ?? ''),
        city: new FormControl(data?.address?.city ?? ''),
        state: new FormControl(data?.address?.state ?? ''),
        postalCode: new FormControl(data?.address?.postalCode ?? ''),
      }),
    });
  }

  addOwner(vehicleIndex: number): void {
    this.getVehicleOwners(vehicleIndex).push(this.createOwnerGroup());
  }

  removeOwner(vehicleIndex: number, ownerIndex: number): void {
    this.getVehicleOwners(vehicleIndex).removeAt(ownerIndex);
  }

  // ============================================================
  // Salvar
  // ============================================================

  save(): void {
    if (this.form.invalid || !this.complaint) return;

    this.saving = true;
    const formValue = this.form.value;
    const vehicles: any[] = formValue.vehicles ?? [];

    // Etapa 1: Cria/atualiza veículos no backend para obter vehicleIds
    this.saveVehicles(vehicles).subscribe({
      next: (vehicleIds) => {
        // Etapa 2: Salva a denúncia com os vehicleIds
        const dto: ComplaintRequestDTO = {
          idDenunciaApp: this.complaint!.idDenunciaApp ? Number(this.complaint!.idDenunciaApp) : undefined,
          address: formValue.address ?? undefined,
          complaintTypeId: formValue.complaintType?.id ?? undefined,
          complaintStatusId: formValue.complaintStatus?.id ?? undefined,
          text: formValue.text ?? undefined,
          registerDate: this.complaint!.registerDate ?? undefined,
          latitude: formValue.latitude ?? undefined,
          longitude: formValue.longitude ?? undefined,
          involvedVehicleIds: vehicleIds,
        };

        this.complaintService.updateComplaint(this.complaint!.id, dto).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Denúncia atualizada com sucesso.' });
            this.saving = false;
            this.router.navigate(['/home/complaints']);
          },
          error: (err) => {
            console.error('Erro ao salvar denúncia:', err);
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível salvar a denúncia.' });
            this.saving = false;
          }
        });
      },
      error: (err) => {
        console.error('Erro ao salvar veículos:', err);
        this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível salvar os veículos.' });
        this.saving = false;
      }
    });
  }

  /**
   * Para cada veículo no formulário:
   * - Se já tem vehicleId → atualiza (PUT)
   * - Se não tem → cria (POST)
   * Retorna array com todos os vehicleIds.
   */
  private saveVehicles(vehicles: any[]): Observable<number[]> {
    if (!vehicles.length) return of([] as number[]);

    const ops = vehicles.map((v: any) => {
      const dto: VehicleInvolvedRequestDTO = {
        plateNumber: v.plateNumber ?? undefined,
        municipality: v.municipality ?? undefined,
        state: v.state ?? undefined,
        vin: v.vin ?? undefined,
        color: v.color ?? undefined,
        brandModel: v.brandModel ?? undefined,
        type: v.type ?? undefined,
        species: v.species ?? undefined,
        category: v.category ?? undefined,
        yearOfManufacture: v.yearOfManufacture ? Number(v.yearOfManufacture) : undefined,
        yearOfModel: v.yearOfModel ? Number(v.yearOfModel) : undefined,
        lastLicensingYear: v.lastLicensingYear ? Number(v.lastLicensingYear) : undefined,
        ownerIds: undefined, // owners seriam criados separadamente se necessário
      };

      if (v.vehicleId) {
        return this.vehicleInvolvedService.update(v.vehicleId, dto).pipe(
          map(() => v.vehicleId)
        );
      } else {
        return this.vehicleInvolvedService.create(dto).pipe(
          map((res) => res.vehicleId)
        );
      }
    });

    return forkJoin(ops);
  }

  cancel(): void {
    this.router.navigate(['/home/complaints']);
  }
}
